import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { QcGuidance, QcGuidanceTone } from './workspaceDerivations';
import { ACCENT_BLUE_DARK, SUCCESS_GREEN } from './qcTheme';

// Next-step strip: blue = the viewer's move, amber = other party working, green = QC done.

const TONE_THEMES: Record<QcGuidanceTone, { fg: string; tint: string; border: string }> = {
  action: { fg: ACCENT_BLUE_DARK, tint: 'rgba(25,118,210,0.08)', border: 'rgba(25,118,210,0.4)' },
  waiting: { fg: '#b26a00', tint: 'rgba(239,148,30,0.1)', border: 'rgba(239,148,30,0.45)' },
  done: { fg: SUCCESS_GREEN, tint: 'rgba(46,125,50,0.08)', border: 'rgba(46,125,50,0.4)' },
};

// Full-tint banner: flat tone wash with color carried by icon/chip/border; the headline stays near-black.
const Banner = styled('div', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: QcGuidanceTone }>(({ tone }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  margin: '0 0 12px',
  padding: '12px 18px',
  borderRadius: '4px',
  border: `1px solid ${TONE_THEMES[tone].border}`,
  background: TONE_THEMES[tone].tint,
}));

const IconRing = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: QcGuidanceTone }>(({ tone }) => ({
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fff',
  border: `1px solid ${TONE_THEMES[tone].border}`,
  color: TONE_THEMES[tone].fg,
  '& .MuiSvgIcon-root': { fontSize: '18px' },
}));

const TextBlock = styled('div')({
  minWidth: 0,
});

const Headline = styled('div')({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
  lineHeight: 1.35,
});

const Detail = styled('div')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.6)',
  lineHeight: 1.45,
  marginTop: '1px',
});

const RightCluster = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  marginLeft: 'auto',
  flex: 'none',
});

const RoundChip = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: QcGuidanceTone }>(({ tone }) => ({
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  height: '24px',
  padding: '0 12px',
  borderRadius: '12px',
  background: '#fff',
  border: `1px solid ${TONE_THEMES[tone].border}`,
  color: TONE_THEMES[tone].fg,
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

const DismissButton = styled('button', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: QcGuidanceTone }>(({ tone }) => ({
  border: 'none',
  background: 'none',
  padding: '4px',
  marginLeft: '2px',
  flex: 'none',
  display: 'inline-flex',
  borderRadius: '50%',
  cursor: 'pointer',
  color: TONE_THEMES[tone].fg,
  opacity: 0.6,
  '&:hover': { opacity: 1, background: 'rgba(0,0,0,0.05)' },
}));

const TONE_ICONS: Record<QcGuidanceTone, JSX.Element> = {
  action: <ArrowForwardIcon />,
  waiting: <HourglassEmptyIcon />,
  done: <CheckCircleIcon />,
};

export interface QcGuidanceBannerProps {
  guidance: QcGuidance | null;
}

export default function QcGuidanceBanner({ guidance }: QcGuidanceBannerProps): JSX.Element | null {
  // Dismissal keys to the headline, so the next step reappears the moment the state changes.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  if (!guidance || dismissedKey === guidance.headline) return null;
  return (
    <Banner tone={guidance.tone} role='status'>
      <IconRing tone={guidance.tone}>{TONE_ICONS[guidance.tone]}</IconRing>
      <TextBlock>
        <Headline>{guidance.headline}</Headline>
        <Detail>{guidance.detail}</Detail>
      </TextBlock>
      <RightCluster>
        {guidance.roundNumber !== undefined && (
          <RoundChip tone={guidance.tone}>Round {guidance.roundNumber}</RoundChip>
        )}
        <DismissButton
          type='button'
          tone={guidance.tone}
          aria-label='Dismiss guidance'
          onClick={() => setDismissedKey(guidance.headline)}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </DismissButton>
      </RightCluster>
    </Banner>
  );
}
